import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { getSourceFiles, getSourceFileContent } from '../services/api';
import { toast } from 'sonner';
import {
  X, FolderOpen, FileText, Loader2, ChevronRight, ChevronDown,
  Code2, FileCode, FileJson, FileType, Braces, Hash, File,
  Search, FolderTree
} from 'lucide-react';

// ---------------------------------------------------------------------------
// File icon helper — pick an icon by extension / category
// ---------------------------------------------------------------------------
const getFileIcon = (filename, category) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const iconClass = "h-3.5 w-3.5 flex-shrink-0";
  if (['js', 'jsx', 'ts', 'tsx'].includes(ext)) return <Braces className={`${iconClass} text-yellow-500`} />;
  if (['py'].includes(ext)) return <FileCode className={`${iconClass} text-blue-500`} />;
  if (['cs', 'java', 'go', 'rs', 'cpp', 'c', 'h'].includes(ext)) return <Code2 className={`${iconClass} text-emerald-500`} />;
  if (['json'].includes(ext)) return <FileJson className={`${iconClass} text-amber-500`} />;
  if (['html', 'htm', 'xml', 'svg'].includes(ext)) return <FileType className={`${iconClass} text-orange-500`} />;
  if (['css', 'scss', 'less'].includes(ext)) return <Hash className={`${iconClass} text-pink-500`} />;
  if (['md', 'txt', 'rst'].includes(ext)) return <FileText className={`${iconClass} text-slate-400`} />;
  if (category === 'config') return <FileJson className={`${iconClass} text-slate-500`} />;
  return <File className={`${iconClass} text-slate-400`} />;
};

// ---------------------------------------------------------------------------
// Category badge colours
// ---------------------------------------------------------------------------
const categoryColors = {
  code: 'bg-blue-100 text-blue-700',
  config: 'bg-amber-100 text-amber-700',
  docs: 'bg-emerald-100 text-emerald-700',
  test: 'bg-purple-100 text-purple-700',
  data: 'bg-orange-100 text-orange-700',
  unknown: 'bg-slate-100 text-slate-600',
};

// ---------------------------------------------------------------------------
// Build folder tree from flat filename list
// ---------------------------------------------------------------------------
const buildTree = (files) => {
  const root = { name: '', children: {}, files: [] };
  for (const f of files) {
    const parts = f.filename.split('/');
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!node.children[parts[i]]) {
        node.children[parts[i]] = { name: parts[i], children: {}, files: [] };
      }
      node = node.children[parts[i]];
    }
    node.files.push({ ...f, displayName: parts[parts.length - 1] });
  }
  return root;
};

// ---------------------------------------------------------------------------
// Human-readable file size
// ---------------------------------------------------------------------------
const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ---------------------------------------------------------------------------
// Folder node component (recursive)
// ---------------------------------------------------------------------------
const FolderNode = ({ name, node, depth, onSelect, selectedFile }) => {
  const [open, setOpen] = useState(depth < 2); // auto-expand top 2 levels
  const hasContent = Object.keys(node.children).length > 0 || node.files.length > 0;
  if (!hasContent) return null;

  const childFolders = Object.entries(node.children).sort(([a], [b]) => a.localeCompare(b));
  const childFiles = [...node.files].sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <div>
      {name && (
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 w-full text-left py-1 px-1 rounded hover:bg-slate-100 transition-colors group"
          style={{ paddingLeft: `${depth * 12 + 4}px` }}
        >
          {open ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
          <FolderOpen className={`h-3.5 w-3.5 ${open ? 'text-amber-500' : 'text-slate-400'}`} />
          <span className="text-xs font-medium text-slate-700 truncate">{name}</span>
          <span className="text-[10px] text-slate-400 ml-auto pr-1">{countFiles(node)}</span>
        </button>
      )}
      {(open || !name) && (
        <div>
          {childFolders.map(([k, child]) => (
            <FolderNode key={k} name={k} node={child} depth={name ? depth + 1 : depth} onSelect={onSelect} selectedFile={selectedFile} />
          ))}
          {childFiles.map((f) => (
            <button
              key={f.filename}
              onClick={() => onSelect(f)}
              className={`flex items-center gap-1.5 w-full text-left py-1 px-1 rounded transition-colors ${
                selectedFile === f.filename ? 'bg-sky-50 text-sky-700' : 'hover:bg-slate-50 text-slate-600'
              }`}
              style={{ paddingLeft: `${(name ? depth + 1 : depth) * 12 + 16}px` }}
            >
              {getFileIcon(f.displayName, f.category)}
              <span className="text-xs truncate flex-1">{f.displayName}</span>
              <span className="text-[10px] text-slate-400 flex-shrink-0">{formatSize(f.size)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Count files recursively in a tree node
const countFiles = (node) => {
  let count = node.files.length;
  for (const child of Object.values(node.children)) {
    count += countFiles(child);
  }
  return count;
};

// ---------------------------------------------------------------------------
// Language detect for syntax highlighting hint
// ---------------------------------------------------------------------------
const getLang = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const map = {
    js: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
    py: 'python', cs: 'csharp', java: 'java', go: 'go', rs: 'rust',
    cpp: 'cpp', c: 'c', h: 'c', rb: 'ruby', php: 'php', swift: 'swift',
    kt: 'kotlin', dart: 'dart', sql: 'sql', sh: 'bash', bash: 'bash',
    json: 'json', xml: 'xml', html: 'html', css: 'css', scss: 'scss',
    yaml: 'yaml', yml: 'yaml', md: 'markdown', txt: 'text',
    csproj: 'xml', sln: 'text', toml: 'toml', ini: 'ini',
  };
  return map[ext] || 'text';
};

// ===========================================================================
// Main Panel
// ===========================================================================
const SourceFilesPanel = ({ docId, isOpen, onClose }) => {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch file list when panel opens
  useEffect(() => {
    if (isOpen && docId) fetchFiles();
    if (!isOpen) {
      setSelectedFile(null);
      setFileContent(null);
      setSearchQuery('');
    }
  }, [isOpen, docId]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await getSourceFiles(docId);
      setFiles(res.data.files || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load source files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFile = async (file) => {
    setSelectedFile(file.filename);
    setLoadingContent(true);
    setFileContent(null);
    try {
      const res = await getSourceFileContent(docId, file.filename);
      setFileContent(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load file content');
    } finally {
      setLoadingContent(false);
    }
  };

  // Category breakdown
  const categoryBreakdown = useMemo(() => {
    const counts = {};
    for (const f of files) {
      const cat = f.category || 'unknown';
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return Object.entries(counts).sort(([, a], [, b]) => b - a);
  }, [files]);

  // Filtered files
  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) return files;
    const q = searchQuery.toLowerCase();
    return files.filter(f => f.filename.toLowerCase().includes(q));
  }, [files, searchQuery]);

  // Build tree from filtered files
  const tree = useMemo(() => buildTree(filteredFiles), [filteredFiles]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[480px] lg:w-[560px] bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200" data-testid="source-files-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/80">
        <div className="flex items-center gap-2">
          <FolderTree className="h-4 w-4 text-sky-600" />
          <h3 className="text-sm font-semibold text-slate-800">Source Files</h3>
          {total > 0 && (
            <Badge variant="secondary" className="text-[10px] h-5">{total} files</Badge>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-200 text-slate-400" data-testid="close-source-files-panel">
          <X className="h-4 w-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-sky-500" />
          <span className="ml-2 text-sm text-slate-500">Loading source files...</span>
        </div>
      ) : files.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 px-6">
          <FolderOpen className="h-10 w-10 mb-3 text-slate-300" />
          <p className="text-sm font-medium">No source files found</p>
          <p className="text-xs mt-1 text-center">Source files are stored when documentation is generated from uploaded code or repository imports.</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Category breakdown */}
          <div className="px-4 py-2 border-b border-slate-100 flex flex-wrap gap-1.5">
            {categoryBreakdown.map(([cat, count]) => (
              <Badge key={cat} className={`text-[10px] ${categoryColors[cat] || categoryColors.unknown}`}>
                {cat} ({count})
              </Badge>
            ))}
          </div>

          {/* Search */}
          <div className="px-4 py-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Filter files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-300 bg-white"
                data-testid="source-files-search"
              />
            </div>
          </div>

          {/* Split view: tree left, content right (on larger screens), stacked on small */}
          <div className="flex-1 flex flex-col min-h-0">
            {!selectedFile ? (
              /* Full-width file tree */
              <ScrollArea className="flex-1">
                <div className="py-1 px-2">
                  <FolderNode name="" node={tree} depth={0} onSelect={handleSelectFile} selectedFile={selectedFile} />
                  {filteredFiles.length === 0 && searchQuery && (
                    <p className="text-xs text-slate-400 text-center py-4">No files match "{searchQuery}"</p>
                  )}
                </div>
              </ScrollArea>
            ) : (
              /* File selected — show tree (collapsible) + content */
              <>
                {/* Breadcrumb / Back bar */}
                <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
                  <button
                    onClick={() => { setSelectedFile(null); setFileContent(null); }}
                    className="text-xs text-sky-600 hover:text-sky-700 font-medium flex items-center gap-1"
                    data-testid="back-to-tree"
                  >
                    <ChevronRight className="h-3 w-3 rotate-180" />
                    Back
                  </button>
                  <span className="text-[10px] text-slate-400">|</span>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {getFileIcon(selectedFile, fileContent?.category)}
                    <span className="text-xs text-slate-600 truncate">{selectedFile}</span>
                  </div>
                  {fileContent && (
                    <div className="ml-auto flex items-center gap-1.5">
                      <Badge className={`text-[10px] ${categoryColors[fileContent.category] || categoryColors.unknown}`}>
                        {fileContent.category}
                      </Badge>
                      <span className="text-[10px] text-slate-400">{formatSize(fileContent.size)}</span>
                    </div>
                  )}
                </div>

                {/* File content */}
                <ScrollArea className="flex-1">
                  {loadingContent ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-5 w-5 animate-spin text-sky-500" />
                      <span className="ml-2 text-sm text-slate-500">Loading file...</span>
                    </div>
                  ) : fileContent ? (
                    <div className="relative">
                      {/* Line numbers + code */}
                      <pre className="text-xs leading-relaxed overflow-x-auto">
                        <code className="block p-3">
                          {fileContent.content.split('\n').map((line, i) => (
                            <div key={i} className="flex hover:bg-slate-50">
                              <span className="select-none text-right pr-3 text-slate-300 w-10 flex-shrink-0 border-r border-slate-100 mr-3">
                                {i + 1}
                              </span>
                              <span className="whitespace-pre">{line}</span>
                            </div>
                          ))}
                        </code>
                      </pre>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
                      Failed to load file content
                    </div>
                  )}
                </ScrollArea>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceFilesPanel;
